import { getICBilledCoverageMetadata, ICBilledCoverageId, ICReportRow } from "@deathstar/types/blazar";
import moment from "moment";
import * as xlsx from "xlsx";

const CURRENCY_FORMAT = '"$"#,##0.00';

export interface ICReportMeta {
    clientName: string;
    policyNumbers: string[];
    /** `month` is 1-indexed */
    period: { year: number; month: number };
    /** the coverage columns to emit, in order */
    coverageOptionIds: ICBilledCoverageId[];
}

const money = (value: number): xlsx.CellObject => ({ t: "n", z: CURRENCY_FORMAT, v: value });

/**
 * Builds the independent-contractor workbook, mirroring the layout of the legacy
 * `getIndependentContractorReport` spreadsheet: an identification block, a blank line, the driver
 * breakdown, then a totals row.
 *
 * The breakdown is a filterable range rather than a true Excel Table — SheetJS's community build
 * writes an autofilter but not a ListObject, so there is no banded styling, no structured
 * references, and the totals row is a static value rather than a SUBTOTAL that follows the filter.
 * Excel's "Format as Table" converts the range in one step.
 */
export function buildICReportWorkbook(meta: ICReportMeta, rows: ICReportRow[]): xlsx.WorkBook {
    const start = moment
        .utc()
        .year(meta.period.year)
        .month(meta.period.month - 1)
        .startOf("month");
    const end = start.clone().endOf("month");

    const header = [
        "Employer Name",
        "Driver Name",
        "State",
        "Tractor VIN(s)",
        "Tractor Value(s)",
        "Trailer VIN(s)",
        "Trailer Value(s)",
        ...meta.coverageOptionIds.map((id) => getICBilledCoverageMetadata(id).name),
        "Total Driver Cost",
        "Driver Add/Removal Date",
        "Tractor Add/Removal Date",
        "Trailer Add/Removal Date",
    ];

    const totals = meta.coverageOptionIds.map((id) => rows.reduce((acc, row) => acc + (row.costs.get(id) ?? 0), 0));
    const grandTotal = rows.reduce((acc, row) => acc + row.total, 0);

    const dataRows = rows.map((row) => [
        row.employer,
        row.driverName,
        row.state,
        row.tractorVins,
        row.tractorValues,
        row.trailerVins,
        row.trailerValues,
        ...meta.coverageOptionIds.map((id) => money(row.costs.get(id) ?? 0)),
        money(row.total),
        row.driverDates,
        row.tractorDates,
        row.trailerDates,
    ]);

    const preamble = [
        ["Client Name:", meta.clientName || "[Client Name]"],
        ["Policy Number(s):", meta.policyNumbers.join(", ") || "[Policy Number]"],
        ["Report Date:", `${start.format("M/D/YYYY")}-${end.format("M/D/YYYY")}`],
        ["Total:", money(grandTotal)],
        [],
    ];

    const sheet = xlsx.utils.aoa_to_sheet([
        ...preamble,
        header,
        ...dataRows,
        [],
        ["Total:", "", "", "", "", "", "", ...totals.map(money), money(grandTotal), "", "", ""],
    ]);

    const headerRow = preamble.length; // 0-indexed row of the header
    const lastRow = headerRow + dataRows.length;

    sheet["!cols"] = [
        { wpx: 200 },
        { wpx: 130 },
        { wpx: 50 },
        { wpx: 90 },
        { wpx: 100 },
        { wpx: 90 },
        { wpx: 100 },
        ...meta.coverageOptionIds.map(() => ({ wpx: 140 })),
        { wpx: 120 },
        { wpx: 170 },
        { wpx: 220 },
        { wpx: 220 },
    ];

    sheet["!autofilter"] = {
        ref: xlsx.utils.encode_range({ s: { r: headerRow, c: 0 }, e: { r: lastRow, c: header.length - 1 } }),
    };

    const book = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(book, sheet, "Driver Breakdown");
    return book;
}

/** Builds the workbook and hands it to the browser as a download. */
export function downloadICReport(meta: ICReportMeta, rows: ICReportRow[]): void {
    const book = buildICReportWorkbook(meta, rows);
    const period = moment
        .utc()
        .year(meta.period.year)
        .month(meta.period.month - 1);
    xlsx.writeFile(book, `${meta.clientName || "IC Report"} - IC Report - ${period.format("MMMM YYYY")}.xlsx`);
}
